import { useState, useEffect } from "react";
import { Row, Col, Card, message, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { admin_impersonateApi } from "./../api/admin.apis";
import { config } from "./../config/env";
// Import API
import { 
  user_managementApi, 
  updateUserApi,
} from "../api/admin.apis";
import { meApi } from "../api/auth.api";
// Import Components
import {
  UserTable,
  UserFilters,
  UserModalView,
  UserModalEdit,
  ImpersonateModal,
  AssignPlanModal,
} from "../components/users-management";

const { confirm } = Modal;

function UserManagement() {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [impersonateModalVisible, setImpersonateModalVisible] = useState(false);
  const [assignPlanModalVisible, setAssignPlanModalVisible] = useState(false);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Loading states
  const [editLoading, setEditLoading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch users data
  useEffect(() => {
    fetchUsersData();
    fetchMe();
  }, []);

  const fetchMe = async () => {
    try {
      const response = await meApi();
      if (response.data.status === "success") {
        const userData = response.data.data;
        setCurrentUser(userData);
      } else {
        message.error("Failed to load current user data");
      }
    } catch (err) {
      console.error("Error fetching current user:", err);
      message.error("Error loading current user data");
    }
  };

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      const response = await user_managementApi();

      if (response.data.status === "success") {
        // Map the API response to include status field based on backend enum
        const users = response.data.data.map(user => ({
          ...user,
          key: user._id,
          // Map is_active and is_verified to status field for the table
          status: user.user_status
        }));
        setUsersData(users);
        filterUsers(searchText, statusFilter, users);
      } else {
        setError("Failed to fetch users data");
        message.error("Failed to load users data");
      }
    } catch (err) {
      console.error("Error fetching users data:", err);
      setError(err.message || "An error occurred");
      message.error("Error loading users data");
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and status
  const filterUsers = (search, filter, users = usersData) => {
    let filtered = users;

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(user =>
        user.full_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower) ||
        user.website_url?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(user => user.user_status === filter);
    }

    setFilteredUsers(filtered);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    filterUsers(value, statusFilter);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    filterUsers(searchText, value);
  };

  const handleImpersonate = (userId) => {
    setSelectedUserId(userId);
    setImpersonateModalVisible(true);
  };

  const confirmImpersonate = async () => {
    if (!selectedUserId) return;
  
    // Open tab immediately (prevents popup blocking)
    const newTab = window.open("", "_blank");
  
    try {
      setImpersonateLoading(true);
  
      const response = await admin_impersonateApi(selectedUserId);
  
      if (response.data.status === "success") {
        const token = response.data.data.access_token;
  
        const impersonationUrl =
          `${config.SenseInsightPublicUrl}/auth/login?token=${token}&impersonate=true`;
  
        newTab.location.href = impersonationUrl;
  
        message.success("Impersonation started successfully");
      } else {
        newTab.close();
        message.error("Failed to impersonate user");
      }
    } catch (error) {
      console.error("Impersonation error:", error);
      newTab.close();
      message.error("Error impersonating user");
    } finally {
      setImpersonateLoading(false);
      setImpersonateModalVisible(false);
      setSelectedUserId(null);
    }
  };

  const cancelImpersonate = () => {
    setImpersonateModalVisible(false);
    setSelectedUserId(null);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setViewModalVisible(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditModalVisible(true);
  };

  const handleAssignPlan = (user) => {
    setSelectedUser(user);
    setAssignPlanModalVisible(true);
  };

  const handleChangeUserStatus = async (user, newStatus) => {
    try {
      // Show confirmation for status changes
      if (newStatus === 'suspended') {
        confirm({
          title: 'Are you sure you want to suspend this user?',
          icon: <ExclamationCircleOutlined />,
          content: `Suspending ${user.full_name || user.email} will restrict their access to the platform.`,
          okText: 'Yes, Suspend',
          okType: 'danger',
          cancelText: 'Cancel',
          async onOk() {
            await updateUserStatus(user._id, newStatus);
          },
        });
      } else if (newStatus === 'deactivate') {
        confirm({
          title: 'Are you sure you want to deactivate this user?',
          icon: <ExclamationCircleOutlined />,
          content: `Deactivating ${user.full_name || user.email} will prevent them from logging in.`,
          okText: 'Yes, Deactivate',
          okType: 'danger',
          cancelText: 'Cancel',
          async onOk() {
            await updateUserStatus(user._id, newStatus);
          },
        });
      } else {
        // For activation, no confirmation needed
        await updateUserStatus(user._id, newStatus);
      }
    } catch (error) {
      console.error('Error changing user status:', error);
      message.error('Error updating user status');
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const response = await updateUserApi(userId, { user_status: newStatus });
      
      if (response.data.status === "success") {
        const statusMessages = {
          'active': 'activated',
          'deactivate': 'deactivated',
          'suspended': 'suspended'
        };
        message.success(`User ${statusMessages[newStatus] || 'updated'} successfully`);
        fetchUsersData();
      } else {
        message.error('Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      message.error('Error updating user status');
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      setEditLoading(true);
      
      // Prepare update data - don't send status if it's not being changed
      const updateData = {
        full_name: values.full_name,
        role: values.role,
        phone_number: values.phone_number,
        website_url: values.website_url,
        is_active: values.is_active,
        is_verified: values.is_verified,
      };

      const response = await updateUserApi(selectedUser._id, updateData);
      
      if (response.data.status === "success") {
        message.success('User updated successfully');
        setEditModalVisible(false);
        fetchUsersData();
      } else {
        message.error('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Error updating user');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Users Management ({filteredUsers.length} users)</span>
                <UserFilters
                  onSearch={handleSearch}
                  onFilterChange={handleFilterChange}
                  onRefresh={fetchUsersData}
                  loading={loading}
                />
              </div>
            }
          >
            <UserTable
              currentUser={currentUser}
              users={filteredUsers}
              loading={loading}
              onViewUser={handleViewUser}
              onEditUser={handleEditUser}
              onChangeUserStatus={handleChangeUserStatus}
              onImpersonate={handleImpersonate}
              onAssignPlan={handleAssignPlan}
            />
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <UserModalView
        visible={viewModalVisible}
        user={selectedUser}
        subscriptionRefreshKey={subscriptionRefreshKey}
        onClose={() => setViewModalVisible(false)}
        onEdit={() => {
          setViewModalVisible(false);
          handleEditUser(selectedUser);
        }}
      />

      <UserModalEdit
        visible={editModalVisible}
        user={selectedUser}
        loading={editLoading}
        onSubmit={handleEditSubmit}
        onClose={() => setEditModalVisible(false)}
      />

      <ImpersonateModal
        visible={impersonateModalVisible}
        loading={impersonateLoading}
        onConfirm={confirmImpersonate}
        onCancel={cancelImpersonate}
      />

      <AssignPlanModal
        visible={assignPlanModalVisible}
        user={selectedUser}
        onClose={() => setAssignPlanModalVisible(false)}
        onSuccess={() => setSubscriptionRefreshKey((k) => k + 1)}
      />
    </div>
  );
}

export default UserManagement;